<template>
  <page-template :is-loading="isLoading" :data-exists="selectedAclProfileData !== null">
    <template #pageHeadline>
      {{
        isNew
          ? $t('aclProfile.configPage.newPageHeadline')
          : $t('aclProfile.configPage.editPageHeadline')
      }}
    </template>
    <template #secondLevelHeaderLeft v-if="!readOnly">
      <rbz-form-button
        variant="tertiary"
        icon="IconArrowBack"
        v-b-tooltip="$t('common.operations.returnToList')"
        link-route-name="ACL Profiles List"
        classes="redirect-to-list-button"
        data-qa="redirect-to-list-button"
      />
      <template v-if="selectedAclProfile && !isNew">
        <div class="vertical-divider">
          <layout-divider-line orientation="vertical" color="light-gray" />
        </div>
        <rbz-dropdown
          :items="dropdownData.map((item) => [item.id, item.name])"
          :disabled="isLoading"
          v-model:value="selectedAclProfile"
          @update:value="switchAclProfile($event)"
          :ignore-update-in-process="true"
          type="single-select"
          :header-tooltip="$t('aclProfile.configPage.switchTooltip')"
          classes="switch-acl-profile switch-page-entry"
          data-qa="switch-acl-profile"
        />
      </template>
    </template>
    <template #secondLevelHeaderRight v-if="!readOnly">
      <template v-if="selectedAclProfile && !isNew">
        <rbz-form-button
          :label="$t('common.operations.duplicate.label')"
          variant="tertiary"
          icon="IconDuplicate"
          @button-click="duplicateAclProfile()"
          class="duplicate-button"
          :disabled="isLoading || entityNotSaved"
          :tooltip="
            entityNotSaved ? $t('common.operations.duplicate.disabledTooltip.notSaved') : ''
          "
          data-qa="duplicate-button"
        />
        <rbz-form-button
          :label="$t('common.operations.download.label')"
          variant="tertiary"
          icon="IconDownload"
          @button-click="downloadAclProfile()"
          class="download-button"
          :disabled="isLoading"
          data-qa="download-button"
        />
        <rbz-form-button
          :label="$t('common.operations.delete.label')"
          variant="tertiary"
          icon="IconDelete"
          @button-click="openDeleteAclProfileModal()"
          class="delete-button"
          :disabled="isLoading || selectedAclProfileProtected || selectedAclProfileReferenced"
          :tooltip="deleteTooltip"
          data-qa="delete-button"
        />
        <div class="vertical-divider">
          <layout-divider-line orientation="vertical" color="light-gray" />
        </div>
      </template>
      <rbz-form-button
        :label="$t('common.operations.save.label')"
        :disabled="isLoading || !isAclProfileValid"
        @button-click="saveChanges()"
        class="save-changes-button"
        data-qa="save-changes-button"
      />
    </template>
    <template #pageContent>
      <div class="rbz-columns">
        <div class="rbz-column is-4">
          <div class="rbz-field">
            <rbz-form-input
              name="name"
              classes="name"
              data-qa="name-input"
              :label="$t('aclProfile.configPage.name')"
              :placeholder="$t('aclProfile.configPage.name')"
              v-model:inputValue="selectedAclProfileData.name"
              :disabled="readOnly"
            />
          </div>
          <div class="rbz-field">
            <rbz-form-textarea
              name="description"
              classes="description"
              data-qa="description-textarea"
              :label="$t('aclProfile.configPage.description')"
              v-model:inputValue="selectedAclProfileData.description"
              rows="2"
              :disabled="readOnly"
            />
          </div>
          <div class="rbz-field">
            <rbz-dropdown
              v-if="actions.length"
              :items="actions.map((item) => [item.id, item.name])"
              v-model:value="selectedAclProfileData.action"
              classes="action-selection"
              :disabled="readOnly"
              data-qa="action-dropdown"
              :label="$t('aclProfile.configPage.action')"
            />
          </div>
          <div class="rbz-field tags-selection-field">
            <tags-selection
              :value="selectedAclProfileData.tags"
              @update:value="selectedAclProfileData.tags = $event"
              :label="$t('aclProfile.configPage.tags')"
              :disabled="readOnly"
            />
            <div class="automatic-tags">
              <template v-for="tag in automaticTags" :key="tag">
                <rbz-custom-tag :value="tag" :disabled="readOnly" />
              </template>
            </div>
          </div>
        </div>
      </div>
      <tags-selection-table
        :columns="tagsSelectionTableColumns"
        @update:tags="selectedAclProfileData[$event.fieldName] = $event.tags"
        :disabled="readOnly"
      />
      <git-history
        :api-path="gitAPIPath"
        ref="gitHistory"
        :restore-target-title="`[${entityName.plural}]`"
        @restore-version="
          loadAclProfile(selectedAclProfile);
          loadDropdownData();
        "
        :disabled="readOnly"
      />
      <rbz-modal-dialog
        :show="showDeleteAclProfileModal"
        title-icon="IconWarning"
        :title="$t('common.operations.delete.modal.title', entityName)"
        :primary-btn-title="$t('common.operations.delete.modal.confirm')"
        @onPrimaryBtnClick="deleteAclProfile"
        :secondary-btn-title="$t('common.operations.delete.modal.cancel')"
        @onSecondaryBtnClick="showDeleteAclProfileModal = false"
        @closeModal="showDeleteAclProfileModal = false"
        size="medium"
      >
        <template #content>
          {{ $t('common.operations.delete.modal.message', { name: selectedAclProfileData.name }) }}
        </template>
      </rbz-modal-dialog>
    </template>
  </page-template>
</template>

<script lang="ts">
import _ from 'lodash';
import { defineComponent } from 'vue';
import {
  AclProfile,
  AclProfileFilter,
  BasicDocument,
  Action,
  TagsSelectionTableColumn,
  TagVariant,
  DocumentName,
} from '@/types';
import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import { useUserStore } from '@/store/UserStore';
import RequestsUtils from '@/helpers/RequestsUtils';
import Utils from 'ui-component-library/helpers/Utils';
import RbzFormButton from '@/components/RbzFormButton.vue';
import RbzDropdown from '@/components/RbzDropdown.vue';
import RbzFormInput from '@/components/RbzFormInput.vue';
import RbzFormTextarea from '@/components/RbzFormTextarea.vue';
import PageTemplate from '@/views/PageTemplate.vue';
import LayoutDividerLine from 'ui-component-library/rbz/components/LayoutDividerLine.vue';
import TagsSelection from '@/components/TagsSelection.vue';
import RbzCustomTag from '@/components/RbzCustomTag.vue';
import GitHistory from '@/components/GitHistory.vue';
import TagsSelectionTable from '@/components/TagsSelectionTable.vue';
import DatasetsUtils from '@/helpers/DatasetsUtils';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import { useTagsStore } from '@/store/TagsStore';
import { useRouting } from '@/helpers/RoutingUtils';

export default defineComponent({
  name: 'AclProfilesEditor',
  setup() {
    const { getEditPath, getListPath, navigateTo, normalizeRouteName } = useRouting();
    return {
      getEditPath,
      getListPath,
      navigateTo,
      normalizeRouteName,
    };
  },
  components: {
    TagsSelectionTable,
    RbzCustomTag,
    RbzFormButton,
    RbzFormInput,
    RbzFormTextarea,
    GitHistory,
    LayoutDividerLine,
    PageTemplate,
    RbzDropdown,
    RbzModalDialog,
    TagsSelection,
  },
  props: {
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dropdownData: [] as BasicDocument[],
      isNew: false,
      selectedAclProfile: null as string,
      selectedAclProfileData: null as AclProfile,
      originalAclProfileData: null as AclProfile,
      pageLoadingCounter: null,
      isDeleteLoading: false,
      showDeleteAclProfileModal: false,

      actions: null as Action[],
      referencedIds: [],
    };
  },
  computed: {
    selectedBranch(): string {
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, useTagsStore, useUserStore),

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    entityName(): DocumentName {
      return {
        plural: this.$t('aclProfile.plural'),
        singular: this.$t('aclProfile.singular'),
      };
    },

    gitAPIPath(): string {
      return `conf/${this.selectedBranch}/acl-profiles/versions`;
    },

    isLoading(): boolean {
      return (
        this.pageLoadingCounter === null || this.pageLoadingCounter > 0 || this.isDeleteLoading
      );
    },

    entityNotSaved(): boolean {
      return !_.isEqual(this.selectedAclProfileData, this.originalAclProfileData);
    },

    selectedAclProfileProtected(): boolean {
      return this.selectedAclProfile?.startsWith('__');
    },

    selectedAclProfileReferenced(): boolean {
      return this.referencedIds.includes(this.selectedAclProfile);
    },

    deleteTooltip(): string {
      if (this.selectedAclProfileProtected) {
        return this.$t('common.operations.delete.disabledTooltip.protected', this.entityName);
      }
      if (this.selectedAclProfileReferenced) {
        return this.$t('common.operations.delete.disabledTooltip.referenced', this.entityName);
      }
      return '';
    },

    automaticTags(): string[] {
      const nameTag = `aclname:${this.tagsStore.textToTag(this.selectedAclProfileData.name)}`;
      return [nameTag];
    },

    duplicateTags(): string[] {
      const aclProfile = this.selectedAclProfileData;
      const allTags = _.concat(
        aclProfile['force_deny'],
        aclProfile['passthrough'],
        aclProfile['allow_bot'],
        aclProfile['deny_bot'],
        aclProfile['allow'],
        aclProfile['deny']
      );
      return _.filter(allTags, (val, i, iteratee) => _.includes(iteratee, val, i + 1));
    },

    tagsSelectionTableColumns(): TagsSelectionTableColumn[] {
      return [
        {
          title: this.$t('aclProfile.configPage.tagsValue.enforceDeny'),
          lists: [
            {
              title: this.$t('common.apply'),
              fieldName: 'force_deny',
              tags: this.selectedAclProfileData['force_deny'],
              tagVariant: (tag: string) => {
                return this.tagVariant('force_deny', tag);
              },
              tagTooltip: (tag: string) => {
                return this.tagTooltip('force_deny', tag);
              },
            },
          ],
          classes: 'is-2',
        },
        {
          title: this.$t('aclProfile.configPage.tagsValue.bypass'),
          lists: [
            {
              title: this.$t('common.apply'),
              fieldName: 'passthrough',
              tags: this.selectedAclProfileData['passthrough'],
              tagVariant: (tag: string) => {
                return this.tagVariant('passthrough', tag);
              },
              tagTooltip: (tag: string) => {
                return this.tagTooltip('passthrough', tag);
              },
            },
          ],
          classes: 'is-2',
        },
        {
          title: this.$t('aclProfile.configPage.tagsValue.botChallenge'),
          lists: [
            {
              title: this.$t('common.skip'),
              fieldName: 'allow_bot',
              tags: this.selectedAclProfileData['allow_bot'],
              tagVariant: (tag: string) => {
                return this.tagVariant('allow_bot', tag);
              },
              tagTooltip: (tag: string) => {
                return this.tagTooltip('allow_bot', tag);
              },
            },
            {
              title: this.$t('common.apply'),
              fieldName: 'deny_bot',
              tags: this.selectedAclProfileData['deny_bot'],
              tagVariant: (tag: string) => {
                return this.tagVariant('deny_bot', tag);
              },
              tagTooltip: (tag: string) => {
                return this.tagTooltip('deny_bot', tag);
              },
            },
          ],
          classes: 'is-4',
        },
        {
          title: this.$t('aclProfile.configPage.tagsValue.block'),
          lists: [
            {
              title: this.$t('common.skip'),
              fieldName: 'allow',
              tags: this.selectedAclProfileData['allow'],
              tagVariant: (tag: string) => {
                return this.tagVariant('allow', tag);
              },
              tagTooltip: (tag: string) => {
                return this.tagTooltip('allow', tag);
              },
            },
            {
              title: this.$t('common.apply'),
              fieldName: 'deny',
              tags: this.selectedAclProfileData['deny'],
              tagVariant: (tag: string) => {
                return this.tagVariant('deny', tag);
              },
              tagTooltip: (tag: string) => {
                return this.tagTooltip('deny', tag);
              },
            },
          ],
          classes: 'is-4',
        },
      ];
    },

    isAclProfileValid(): boolean {
      const noDuplicates = this.duplicateTags.length === 0;
      const trimmedName = this.selectedAclProfileData.name.toLowerCase().trim();
      const nameExists = this.dropdownData.filter((item) => {
        return item.name.toLowerCase().trim() === trimmedName;
      }).length;
      const nameValid =
        this.selectedAclProfileData.name &&
        (trimmedName === this.originalAclProfileData.name.toLowerCase().trim() || !nameExists);
      return noDuplicates && nameValid;
    },
  },
  methods: {
    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    redirectToList() {
      this.navigateTo(this.getListPath('acl-profiles'));
    },

    switchAclProfile(aclProfileId: string) {
      this.navigateTo(this.getEditPath('acl-profiles', aclProfileId));
    },

    async loadDropdownData() {
      this.setPageLoadingStatus(true);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/acl-profiles`,
        config: { headers: { 'x-fields': 'id, name' } },
      });
      this.dropdownData = Utils.sortArrayByName(response?.data?.items) as BasicDocument[];
      this.setPageLoadingStatus(false);
    },

    newAclProfile(): AclProfile {
      const factory = DatasetsUtils.newEntityFactory['acl-profiles'];
      return factory && factory();
    },

    async loadAclProfile(aclProfileId: string) {
      this.setPageLoadingStatus(true);
      this.selectedAclProfile = aclProfileId;
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/acl-profiles/${aclProfileId}`,
      });
      this.selectedAclProfileData = response?.data || {};
      this.originalAclProfileData = _.cloneDeep(this.selectedAclProfileData);
      this.setPageLoadingStatus(false);
    },

    async duplicateAclProfile() {
      this.setPageLoadingStatus(true);
      const aclProfileToAdd = _.cloneDeep(this.selectedAclProfileData);
      aclProfileToAdd.id = DatasetsUtils.generateUUID2();
      aclProfileToAdd.name = Utils.generateUniqueEntityName(
        aclProfileToAdd.name,
        _.map(this.dropdownData, 'name'),
        true
      );
      const successMessage = this.$t(
        'common.operations.duplicate.messages.success',
        this.entityName
      );
      const failureMessage = this.$t(
        'common.operations.duplicate.messages.failure',
        this.entityName
      );
      const url = `conf/${this.selectedBranch}/acl-profiles/${aclProfileToAdd.id}`;
      const response = await RequestsUtils.sendRequest({
        methodName: 'POST',
        url,
        data: aclProfileToAdd,
        successMessage,
        failureMessage,
      });
      if (response) {
        this.switchAclProfile(aclProfileToAdd.id);
      }
      this.setPageLoadingStatus(false);
    },

    downloadAclProfile() {
      Utils.downloadFile('acl-profile', 'json', this.selectedAclProfileData);
    },

    openDeleteAclProfileModal() {
      this.showDeleteAclProfileModal = true;
    },

    async deleteAclProfile() {
      this.isDeleteLoading = true;
      const aclProfileId = this.selectedAclProfile;
      const dropdownIndex = _.findIndex(this.dropdownData, (item) => {
        return item.id === aclProfileId;
      });
      this.dropdownData.splice(dropdownIndex, 1);
      this.selectedAclProfile = null;
      const successMessage = this.$t('common.operations.delete.messages.success', this.entityName);
      const failureMessage = this.$t('common.operations.delete.messages.failure', this.entityName);
      const response = await RequestsUtils.sendRequest({
        methodName: 'DELETE',
        url: `conf/${this.selectedBranch}/acl-profiles/${aclProfileId}`,
        successMessage,
        failureMessage,
      });
      if (response) {
        this.redirectToList();
      }
      this.isDeleteLoading = false;
    },

    async saveChanges() {
      this.setPageLoadingStatus(true);
      let successMessage;
      let failureMessage;
      if (this.isNew) {
        successMessage = this.$t('common.operations.new.messages.success', this.entityName);
        failureMessage = this.$t('common.operations.new.messages.failure', this.entityName);
      } else {
        successMessage = this.$t('common.operations.save.messages.success', this.entityName);
        failureMessage = this.$t('common.operations.save.messages.failure', this.entityName);
      }
      this.selectedAclProfileData.name = this.selectedAclProfileData.name.trim();
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: this.isNew ? 'POST' : 'PUT',
        url: `conf/${this.selectedBranch}/acl-profiles/${this.selectedAclProfileData.id}`,
        data: this.selectedAclProfileData,
        successMessage,
        failureMessage,
        displayServerFailureMessage: true,
      });
      if (response) {
        if (this.isNew) {
          const currentRoute = this.$route.path;
          const targetRoute = currentRoute.replace(
            'new',
            `config/${this.selectedAclProfileData.id}`
          );
          this.$router.push(targetRoute);
        } else {
          await this.loadDropdownData();
          this.originalAclProfileData = _.cloneDeep(this.selectedAclProfileData);
        }
      }
      this.setPageLoadingStatus(false);
    },

    async loadReferencedIds() {
      this.setPageLoadingStatus(true);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/security-policies`,
      });
      const securityPolicies = response?.data?.items || [];
      const referencedIds: string[] = [];

      _.forEach(securityPolicies, (securityPolicy) => {
        _.forEach(securityPolicy.map, (location) => {
          referencedIds.push(location['acl_profile']);
        });
      });
      this.referencedIds = _.uniq(_.flatten(referencedIds));
      this.setPageLoadingStatus(false);
    },

    async loadActions() {
      this.setPageLoadingStatus(true);
      const displayedDefaultIds = [
        'action-acl-block',
        'action-waap-feed-block',
        'action-https-redirect',
      ];
      const displayedTypes: Action['type'][] = ['block'];
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/actions`,
        config: { headers: { 'x-fields': 'id, name, type' } },
      });
      this.actions = _.filter(response?.data?.items, (item: Action) => {
        const hiddenDefaultId =
          item.id.startsWith('action-') && !displayedDefaultIds.includes(item.id);
        return displayedTypes.includes(item.type) && !hiddenDefaultId;
      });
      this.setPageLoadingStatus(false);
    },

    tagTooltip(list: AclProfileFilter, tag: string): string {
      if (this.allPrior(list)) {
        return '[all] is set in a higher priority section';
      }
      if (this.duplicateTags.includes(tag)) {
        return `[${tag}] is duplicated`;
      }
      return tag;
    },

    tagVariant(list: AclProfileFilter, tag: string): TagVariant {
      if (this.allPrior(list)) {
        return 'strikethrough';
      }
      if (this.duplicateTags.includes(tag)) {
        return 'error';
      }
      return 'fill';
    },

    // returns true if tag "all" is set in a higher priority section
    allPrior(filter: AclProfileFilter): boolean {
      const operations: AclProfileFilter[] = [
        'force_deny',
        'passthrough',
        'allow_bot',
        'deny_bot',
        'allow',
        'deny',
      ];
      // top priority, skip
      if (filter === 'force_deny') {
        return false;
      }
      const filterIndex = _.indexOf(operations, filter);
      for (let index = 0; index < filterIndex; index++) {
        if (this.selectedAclProfileData[operations[index]].includes('all')) {
          if (index === 3) {
            return false;
          }
          if (index === 2) {
            return filterIndex === 3;
          }
          return true;
        }
      }
    },

    setSessionDataWatcher() {
      this.$watch(
        () => [this.selectedAclProfileData, this.entityNotSaved],
        (params) => {
          const [formData, entityNotSaved] = params;
          Utils.saveSessionData(formData, entityNotSaved);
        },
        {
          deep: true,
          immediate: true,
        }
      );
    },
  },
  created() {
    const tagStore = useTagsStore();
    this.$watch(
      () => [tagStore.editorDocId, tagStore.editorDocType],
      async ([docId, docType]) => {
        if (!docId || docType !== 'acl-profiles') {
          return;
        }
        await this.branchesStore.getBranches();
        await this.loadActions();
        await this.loadAclProfile(docId);
      },
      { immediate: true }
    );
    this.$watch(
      () => [this.$route.params?.['branch'], this.$route.params?.['doc_id']],
      async (routeParams) => {
        const [_newBranch, newId] = routeParams;
        const routeName: string = this.normalizeRouteName(this.$route.name as string);
        if (['New ACL Profile', 'ACL Profiles Editor'].includes(routeName)) {
          this.setPageLoadingStatus(true);
          this.isNew = routeName.startsWith('New');
          await this.branchesStore.getBranches();
          await this.loadDropdownData();
          const dropdownDataPromise = this.loadDropdownData();
          const actionsPromise = this.loadActions();
          await Promise.all([dropdownDataPromise, actionsPromise]);
          if (this.isNew) {
            this.selectedAclProfileData = this.newAclProfile();
            this.selectedAclProfileData.name = Utils.generateUniqueEntityName(
              this.selectedAclProfileData.name,
              _.map(this.dropdownData, 'name')
            );
            this.originalAclProfileData = _.cloneDeep(this.selectedAclProfileData);
          } else {
            const aclProfileId = newId as string;
            const dataExists = this.dropdownData.some((item) => {
              return item.id === aclProfileId;
            });
            await this.loadReferencedIds();
            if (dataExists) {
              await this.loadAclProfile(aclProfileId);
            } else {
              this.redirectToList();
              return;
            }
          }
          const savedSessionData = sessionStorage.getItem('formData');
          if (savedSessionData) {
            this.selectedAclProfileData = JSON.parse(savedSessionData);
          }
          this.setSessionDataWatcher();
          this.setPageLoadingStatus(false);
        }
      },
      {
        immediate: true,
      }
    );
  },
  beforeRouteLeave() {
    Utils.clearSessionData();
  },
});
</script>
